import { useEffect, useRef } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Detail from "./components/indicator/CountryDetail";
import { useParams } from 'react-router-dom';
import HomePage from "./components/HomePage";
import { FiltersProvider } from "./store/filters-options-context"
import Header from "./global/Header";
import { ColorModeContext, useMode } from "./theme";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import RepositoryFilesList from "./components/RepositoryList";
import FactsheetPage from "./components/factsheet/FactsheetPage";
import BulkDownload from "./components/BulkPage";
import Resources from "./components/Resources";
import TagManager from 'react-gtm-module';
import packageJson from '../package.json';
import { useAuth } from "react-oidc-context";

// Per-chart loading: every indicator card shows its own pulsing placeholder while it
// fetches, so the rest of the page stays readable. Set to false to go back to the previous
// behaviour - a single page-wide overlay that blocks the whole view until every chart has
// answered.
export const PER_CHART_LOADING = true;

// Debug wrapper for Detail to log route parameters
const DebugDetailWrapper = () => {
    const params = useParams();
    console.log('DebugDetailWrapper params:', params);
    return <Detail perChartLoading={PER_CHART_LOADING} />;
};

// Log build version (increment version in package.json on each change)
console.log(`App build version: ${packageJson.version}`);

// Counter for pageview logs
let pageViewCount = 0;

const tagManagerArgs = {
    gtmId: 'GTM-NHPTQQS'
};

TagManager.initialize(tagManagerArgs);


function App() {
    const auth = useAuth();
    const [theme, colorMode] = useMode();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Use ref to store the previous non-resources location
    const previousLocation = useRef(location);
    
    // Track if we're on the resources page
    const isResourcesPage = location.pathname === '/resources';
    
    // Determine which location to use for rendering background
    let backgroundLocation = null;
    if (isResourcesPage) {
        // If we have a location from navigation state, use it
        if (location.state?.from) {
            backgroundLocation = location.state.from;
        } 
        // Otherwise, use the previous location we stored
        else if (previousLocation.current && previousLocation.current.pathname !== '/resources') {
            backgroundLocation = previousLocation.current;
        }
        // Fallback to homepage
        else {
            backgroundLocation = { pathname: '/' };
        }
    } else {
        // Update the previous location ref when not on resources page
        previousLocation.current = location;
    }

    useEffect(() => {
        const path = location.pathname + location.search;
        pageViewCount++;
        console.log(`Pageview effect #${pageViewCount} - location change:`, path);
        const sendPageViewToGTM = (url) => {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'pageType',
                    cmsType: 'CUSTOM',
                    page: {
                        url: window.location.href,
                        path: url,
                    },
                },
            });
        };
        sendPageViewToGTM(path);
    }, [location]);

    useEffect(() => {
        if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator) {
            // Preserve current URL for redirect after authentication
            const currentUrl = window.location.href;
            auth.signinRedirect({ 
                state: { returnUrl: currentUrl }
            });
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.activeNavigator, auth.signinRedirect]);

    useEffect(() => {
        if (auth.isAuthenticated && window.location.search.includes('code=')) {
            // Check if there's a returnUrl in the auth user state
            const returnUrl = auth.user?.state?.returnUrl;
            if (returnUrl) {
                // Redirect to the original URL
                window.location.href = returnUrl;
            } else {
                // Fallback
                const pathname = window.location.pathname;
                const trimmedPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
                const basePath = trimmedPath ? `${trimmedPath}/` : '/';
                const cleanHash = window.location.hash.startsWith('#/')
                    ? window.location.hash
                    : `#/${window.location.hash.replace(/^#/, '')}`;
                const cleanUrl = `${window.location.origin}${basePath}${cleanHash}`;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        }
    }, [auth.isAuthenticated]);

    useEffect(() => {
        if (auth.error) {
            const timer = setTimeout(() => {
                window.location.href = '/';
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [auth.error]);

    if (auth.isLoading) {
        return <div>Loading...</div>;
    }

    if (auth.error) {
        return <div>Encountering error... {auth.error.message}. Redirecting to homepage...</div>;
    }

    if (!auth.isAuthenticated) {
        return <div>Redirecting to sign in...</div>;
    }

    const locationToRender = isResourcesPage && backgroundLocation ? backgroundLocation : location;

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <div className="app">
                    <FiltersProvider>
                        <Header />
                        {/* Render the background page */}
                        <Routes location={locationToRender}>
                            <Route path="/" element={<HomePage />} />
                            <Route path="indicators/:section/:subCat/:countryId/:years/:scroll?" element={<DebugDetailWrapper />} />
                            <Route path="charts/:type/:years/:section/:subCat/:country/:countryId/:anchor" element={<DebugDetailWrapper />} />
                            <Route path="files/:type/:section/:fileCategoryId/:countryId" element={<RepositoryFilesList />} />
                            {/* Shareable deep link to a single country factsheet, e.g. #/factsheet/ALB */}
                            <Route path="factsheet/:iso3" element={<FactsheetPage />} />
                            <Route path=":type/:countryId/:years" element={<BulkDownload />} />
                        </Routes>
                        {/* Render Resources as an overlay when on /resources */}
                        {isResourcesPage && (
                            <Resources onClose={() => navigate(backgroundLocation?.pathname || '/')} />
                        )}
                    </FiltersProvider>
                </div>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
